import { useEffect, useState, useSyncExternalStore } from 'react'
import { QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import { newTurnId } from './api'
import { assistantArtifactLinks } from './artifact-links'
import { assistantActionDisplay, assistantText, type AssistantConversation, type ChatMessage } from './conversation'
import { createLiveSession, reportLiveUsage, type LiveSessionRequest, type LiveTranscript } from './voice-api'

// Live voice (demo `live-voice.ts`): a GPT-Live WebRTC session that listens and speaks while the
// same assistant does the work. Speech delegates each request back here; it runs as an ordinary
// voice turn, and its progress, action cards and outcome are appended to the session as context
// and short spoken results. Brief drops recover on the same peer, then on a new session after
// backed-off attempts; a completed request is never replayed.

type LiveStatus = 'idle' | 'connecting' | 'reconnecting' | 'listening' | 'speaking' | 'closing' | 'error'
type LiveState = { status: LiveStatus; error: string; playbackBlocked: boolean }
type Options = {
  api: QuoteBuilderApi
  context: () => Omit<LiveSessionRequest, 'sdp'> | Promise<Omit<LiveSessionRequest, 'sdp'>>
  transcript: (fragment: LiveTranscript) => void
  delegate: (sessionId: string, id: string, offset: number, started: (message: ChatMessage) => void) => Promise<ChatMessage | undefined>
}
type Append = {
  key: string
  type: string
  content: string
  delegation: string | null
  session: string
  version?: number
  current?: () => boolean
}
// Provider events are untyped JSON; each branch reads only the fields it checks.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LiveEvent = Record<string, any>

/** Backoff for a lost session before voice stops and offers a manual reconnect. */
export const RECONNECT_DELAYS = [250, 1000, 3000, 5000]

// A conservative UTF-8 bound keeps even non-English appends below 500 tokens.
export function liveChunks(value: string, spoken = true) {
  const text = (spoken ? assistantText(value).replace(/https?:\/\/\S+/g, '').replace(/[*#`_>|]/g, '') : value)
    .replace(/\s+/g, ' ')
    .trim()
  const chunks: string[] = []
  let result = ''
  for (const char of text) {
    if (new TextEncoder().encode(result + char).length > 480) {
      chunks.push(result)
      result = ''
    }
    result += char
  }
  if (result) chunks.push(result)
  return chunks
}

export function liveText(value: string, spoken = true) {
  return liveChunks(value, spoken)[0] || ''
}

function fieldText(fields: Array<{ label: string; before: string | null; after: string }>) {
  return fields.map((field) => `${field.label}: ${field.before !== null ? `${field.before} to ` : ''}${field.after}`).join('; ')
}

export function liveDetails(message: ChatMessage) {
  const details = [
    message.running
      ? `Background work is running, not complete. ${message.progress || ''}.`
      : message.turnStatus
        ? `Request status: ${message.turnStatus}.`
        : `Progress (silent context): ${message.progress || 'Request accepted'}.`,
  ]
  for (const action of message.actions || []) {
    const display = assistantActionDisplay(action)
    details.push(`${display.title}: ${action.status}. ${assistantText(action.result || '')} ${assistantText(action.error || '')}`)
    for (const product of display.products) {
      details.push(`${product.manufacturer || ''} ${product.title}, SKU ${product.sku || 'unavailable'}. ${fieldText(product.fields)}.`)
    }
    details.push(fieldText(display.fields))
    if (!display.products.length && !display.fields.length) details.push(display.detail)
  }
  details.push(assistantText(message.content), ...(message.questions || []))
  const links = assistantArtifactLinks(message.artifacts)
  if (links.length) details.push(`Available documents: ${links.map((link) => link.label).join('; ')}.`)
  return details.filter(Boolean).join('\n')
}

export function spokenResult(message: ChatMessage) {
  if (message.running) return 'I have the request. I am building the quote and will keep you updated.'
  if (message.error || message.turnStatus === 'interrupted') {
    return 'The request stopped before finishing. Applied changes are saved. Use Retry below to continue.'
  }
  if (message.actions?.some((action) => action.status === 'stale')) {
    return 'The quote changed after that proposal. Ask me to prepare it again so you can review the current details.'
  }
  if (message.actions?.some((action) => action.status === 'failed')) {
    return 'That change could not be applied. Review the card below before retrying.'
  }
  const pending = message.actions?.filter((action) => ['pending', 'failed', 'stale'].includes(action.status)) || []
  if (pending.length) {
    const display = assistantActionDisplay(pending[0]!)
    return [
      `Proposed, not applied: ${display.title}.`,
      fieldText(display.fields),
      ...(message.questions || []),
      'Approve or reject the card below; voice can stay open.',
    ]
      .filter(Boolean)
      .join(' ')
  }
  return (
    assistantText([message.content, ...(message.questions || [])].join(' ')) ||
    'Explain the verified result and any remaining question using the details provided.'
  )
}

export function createLiveVoice(options: Options) {
  let state: LiveState = { status: 'idle', error: '', playbackBlocked: false }
  const listeners = new Set<() => void>()
  let generation = 0
  let run = 0
  let connected = false
  let retries = 0
  let peer: RTCPeerConnection | undefined
  let channel: RTCDataChannel | undefined
  let microphone: MediaStream | undefined
  let audio: HTMLAudioElement | undefined
  let audioContext: AudioContext | undefined
  let meter: ReturnType<typeof setInterval> | undefined
  let timeout: ReturnType<typeof setTimeout> | undefined
  let recovery: ReturnType<typeof setTimeout> | undefined
  let stable: ReturnType<typeof setTimeout> | undefined
  let abort: AbortController | undefined
  let ready = false
  let sessionId = ''
  let inputVersion = 0
  // Billed duration of the current session, reported for the AI Costs page.
  let usage = { session: '', startedAt: 0, seconds: 0, reported: 0, final: false }
  const events = new Set<string>()
  const tasks = new Set<string>()
  const results = new Map<
    number,
    { sessionId: string; delegation: string; spoken: string; version: number; message: ChatMessage; working: boolean }
  >()
  const appends = new Map<string, Append>()
  const appended = new Map<string, string>()
  let batch: { append: Append; chunks: string[]; index: number } | undefined
  let inFlight: { id: string; type: string } | undefined
  let appendBlocked = false
  let currentContext = ''
  const publish = (changes: Partial<LiveState>) => {
    state = { ...state, ...changes }
    listeners.forEach((listener) => listener())
  }
  const reportUsage = (final = false) => {
    if (!usage.session || usage.final) return
    // A session lost without its final count is billed at least for the time it was open.
    const seconds = final ? usage.seconds : Math.max(usage.seconds, Math.round((Date.now() - usage.startedAt) / 1000))
    if (seconds <= usage.reported && !final) return
    usage.reported = seconds
    usage.final = final
    void reportLiveUsage(options.api, usage.session, seconds, final)
  }
  const cleanup = (keepMedia = false) => {
    reportUsage()
    generation++
    ready = false
    if (keepMedia && batch && !appends.has(batch.append.key)) appends.set(batch.append.key, batch.append)
    batch = undefined
    inFlight = undefined
    appendBlocked = false
    appended.clear()
    if (!keepMedia) {
      appends.clear()
      results.clear()
    }
    clearTimeout(timeout)
    clearInterval(meter)
    clearTimeout(recovery)
    clearTimeout(stable)
    recovery = undefined
    abort?.abort()
    if (!keepMedia) {
      microphone?.getTracks().forEach((track) => track.stop())
      microphone = undefined
    }
    const oldChannel = channel
    channel = undefined
    oldChannel?.close()
    const oldPeer = peer
    peer = undefined
    oldPeer?.close()
    audio?.pause()
    if (audio) audio.srcObject = null
    if (!keepMedia) {
      void audioContext?.close().catch(() => {})
      audioContext = undefined
      audio = undefined
    }
  }
  const fail = (error: string) => {
    run++
    cleanup()
    publish({ status: 'error', error, playbackBlocked: false })
  }
  const ended = () => {
    cleanup()
    publish({ status: 'idle', error: '', playbackBlocked: false })
  }
  const reconnect = () => {
    if (state.status === 'closing') {
      ended()
      return
    }
    // Replace only the media session, never replay the business request. Bound rapid failures.
    const delay = RECONNECT_DELAYS[retries++]
    if (!connected || delay === undefined) {
      fail('Voice could not reconnect. Check your connection, then try Live voice again. Your chat and applied changes are saved.')
      return
    }
    if (ready && channel?.readyState === 'open') {
      try {
        channel.send(JSON.stringify({ type: 'session.close', event_id: newTurnId() }))
      } catch {
        /* The transport may already be gone. */
      }
    }
    cleanup(true)
    publish({ status: 'reconnecting', error: '' })
    recovery = setTimeout(() => {
      recovery = undefined
      void connect()
    }, delay)
  }
  const flush = () => {
    if (inFlight || appendBlocked || !ready || state.status === 'closing' || state.status === 'reconnecting') return
    if (channel?.readyState !== 'open') return
    if (!batch) {
      const append = appends.get('context') || appends.values().next().value
      if (!append) return
      appends.delete(append.key)
      batch = { append, chunks: liveChunks(append.content, append.type === 'session.commentary.append'), index: 0 }
    }
    const { append, chunks, index } = batch
    const superseded = (append.version !== undefined && append.version !== inputVersion) || append.current?.() === false
    const type = append.type === 'session.commentary.append' && superseded ? 'session.thinking.append' : append.type
    try {
      const id = newTurnId()
      inFlight = { id, type }
      channel.send(
        JSON.stringify({
          type,
          event_id: id,
          delegation_id: append.session === sessionId ? append.delegation : null,
          content: chunks[index],
        }),
      )
    } catch {
      reconnect()
    }
  }
  const send = (key: string, type: string, content: string, delegation: string | null = null, version?: number, current?: () => boolean) => {
    if (!content.trim() || ['idle', 'error', 'closing'].includes(state.status)) return
    const signature = JSON.stringify([type, content])
    if (
      (batch?.append.key !== key && appended.get(key) === signature) ||
      (batch?.append.key === key && JSON.stringify([batch.append.type, batch.append.content]) === signature)
    ) {
      appends.delete(key)
      return
    }
    // Replace unsent progress for this key; actions and final speech have separate keys.
    appends.set(key, { key, type, content, delegation, session: sessionId, version, current })
    flush()
  }
  const result = (message: ChatMessage, working = false) => {
    const task = results.get(message.id)
    if (!task) return
    task.message = message
    task.working = working
    const delegation = task.sessionId === sessionId ? task.delegation : null
    send(`turn:${message.id}`, 'session.thinking.append', liveDetails({ ...message, actions: [] }), delegation)
    for (const action of message.actions || []) {
      // A progress change must not resend every previous equipment/configuration card.
      const details = liveDetails({ id: message.id, role: 'assistant', content: '', actions: [action] })
        .split('\n')
        .slice(1)
        .join('\n')
      send(`action:${message.id}:${action.id}`, 'session.thinking.append', details, delegation)
    }
    if ((!message.turnStatus && !message.error && !message.running) || message.actions?.some((action) => action.status === 'applying')) {
      return
    }
    if (working) return
    const content = spokenResult(message)
    const spoken = JSON.stringify([content, message.running ? 'running' : message.actions?.map((action) => [action.id, action.status])])
    if (spoken === task.spoken) return
    // Later speech may correct this request. Preserve its real outcome quietly, not as a new promise.
    task.spoken = spoken
    send(
      `speech:${message.id}:${spoken}`,
      'session.commentary.append',
      content,
      delegation,
      task.version,
      () => task.spoken === spoken && !task.message.actions?.some((action) => action.status === 'applying'),
    )
  }
  const stop = () => {
    if (state.status === 'idle' || state.status === 'error' || state.status === 'closing') return
    run++
    if (!ready || state.status === 'reconnecting' || channel?.readyState !== 'open') {
      ended()
      return
    }
    publish({ status: 'closing' })
    appends.clear()
    batch = undefined
    inFlight = undefined
    microphone?.getTracks().forEach((track) => {
      track.enabled = false
    })
    try {
      channel.send(JSON.stringify({ type: 'session.close', event_id: newTurnId() }))
    } catch {
      ended()
      return
    }
    timeout = setTimeout(ended, 8000)
  }
  const play = async () => {
    try {
      await audioContext?.resume()
      await audio?.play()
      publish({ playbackBlocked: false })
    } catch {
      publish({ playbackBlocked: true })
    }
  }
  const start = async () => {
    if (!['idle', 'error'].includes(state.status)) return
    cleanup()
    run++
    connected = false
    retries = 0
    events.clear()
    tasks.clear()
    results.clear()
    inputVersion = 0
    sessionId = ''
    publish({ status: 'connecting', error: '', playbackBlocked: false })
    await connect()
  }
  const connect = async (): Promise<void> => {
    const current = generation
    const currentRun = run
    events.clear()
    tasks.clear()
    sessionId = ''
    abort = new AbortController()
    const signal = abort.signal
    try {
      if (!navigator.mediaDevices?.getUserMedia || !globalThis.RTCPeerConnection) {
        throw new Error(window.isSecureContext ? 'unsupported' : 'insecure_page')
      }
      audio ??= new Audio()
      audio.autoplay = true
      audioContext ??= new AudioContext()
      void audioContext.resume().catch(() => {})
      const connection = new RTCPeerConnection()
      peer = connection
      connection.addEventListener('track', (event) => {
        if (current !== generation) return
        const stream = new MediaStream([event.track])
        audio!.srcObject = stream
        void play()
        const analyser = audioContext!.createAnalyser()
        analyser.fftSize = 256
        audioContext!.createMediaStreamSource(stream).connect(analyser)
        const samples = new Uint8Array(analyser.fftSize)
        let heardAt = 0
        meter = setInterval(() => {
          if (!ready || state.status === 'closing' || state.status === 'reconnecting') return
          analyser.getByteTimeDomainData(samples)
          if (samples.some((value) => Math.abs(value - 128) > 3)) heardAt = Date.now()
          const status = Date.now() - heardAt < 350 && !audio?.paused ? 'speaking' : 'listening'
          if (state.status !== status) publish({ status })
        }, 100)
      })
      connection.addEventListener('connectionstatechange', () => {
        if (current !== generation) return
        if (connection.connectionState === 'connected') {
          clearTimeout(recovery)
          recovery = undefined
          if (ready && state.status === 'reconnecting') {
            publish({ status: 'listening' })
            results.forEach((task) => result(task.message, task.working))
            flush()
          }
        } else if (connection.connectionState === 'disconnected') {
          if (state.status === 'closing') {
            ended()
            return
          }
          // WebRTC can recover on the same peer after a brief network interruption.
          publish({ status: 'reconnecting' })
          recovery ??= setTimeout(reconnect, 3000)
        } else if (connection.connectionState === 'failed' || connection.connectionState === 'closed') reconnect()
      })
      const stream =
        microphone ||
        (await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }))
      if (current !== generation) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      microphone = stream
      // Permission time is user-controlled; allow ICE plus the backend's 30-second provider wait.
      timeout = setTimeout(
        () => (connected ? reconnect() : fail('Voice could not connect. Check your connection and try again.')),
        45000,
      )
      stream.getAudioTracks().forEach((track) => connection.addTrack(track, stream))
      const data = connection.createDataChannel('oai-events')
      channel = data
      data.addEventListener('close', () => {
        if (current === generation) reconnect()
      })
      data.addEventListener('message', ({ data: raw }) => {
        if (current !== generation) return
        let event: LiveEvent
        try {
          event = JSON.parse(raw)
        } catch {
          fail('Voice received an unreadable response. Reconnect to continue.')
          return
        }
        if (event.event_id && events.has(event.event_id)) return
        if (event.event_id) events.add(event.event_id)
        if (event.type === 'session.started') {
          if (ready || state.status === 'closing') return
          sessionId = event.session.id
          usage = { session: sessionId, startedAt: Date.now(), seconds: 0, reported: 0, final: false }
          clearTimeout(timeout)
          connected = true
          stable = setTimeout(() => {
            retries = 0
          }, 30000)
          publish({ status: 'listening' })
          if (currentContext) send('context', 'session.thinking.append', currentContext)
          results.forEach((task) => result(task.message, task.working))
          ready = true
          flush()
        } else if (event.type === 'session.thinking.appended' || event.type === 'session.commentary.appended') {
          if (inFlight && event.client_event_id === inFlight.id && event.type === `${inFlight.type}ed` && batch) {
            inFlight = undefined
            batch.index++
            if (batch.index === batch.chunks.length) {
              appended.set(batch.append.key, JSON.stringify([batch.append.type, batch.append.content]))
              batch = undefined
            }
          } else if (appendBlocked) {
            appendBlocked = false
            if (batch && appends.has(batch.append.key)) batch = undefined
          }
          flush()
        } else if (event.type === 'session.usage.updated') {
          const seconds = Number(event.usage?.seconds)
          if (Number.isFinite(seconds) && usage.session === sessionId) {
            usage.seconds = Math.max(usage.seconds, seconds)
            // Report as it grows so a closed tab loses at most a minute of recorded cost.
            if (usage.seconds - usage.reported >= 60) reportUsage()
          }
        } else if (event.type === 'session.closed') {
          const seconds = Number(event.usage?.seconds)
          if (Number.isFinite(seconds) && usage.session === sessionId) usage.seconds = Math.max(usage.seconds, seconds)
          reportUsage(true)
          reconnect()
        } else if (event.type === 'error') {
          if (state.status === 'closing') ended()
          else if (event.error?.code === 'too_many_pending_appends' && inFlight && event.error.client_event_id === inFlight.id) {
            // A rejected chunk has no acknowledgement. Wait for the provider's next
            // appended event before retrying it, rather than restarting the voice call.
            inFlight = undefined
            appendBlocked = true
          } else fail('Voice could not continue. Reconnect or use chat; applied changes are saved.')
        } else if (event.type === 'session.input_transcript.delta' || event.type === 'session.output_transcript.delta') {
          if (typeof event.delta !== 'string' || !Number.isFinite(event.start_ms) || !Number.isFinite(event.end_ms)) return
          const role = event.type === 'session.input_transcript.delta' ? 'user' : 'assistant'
          if (role === 'user') inputVersion++
          options.transcript({ event_id: event.event_id, sessionId, role, delta: event.delta, start_ms: event.start_ms, end_ms: event.end_ms })
        } else if (
          event.type === 'session.delegation.created' &&
          event.delegation?.target === 'client' &&
          Number.isFinite(event.offset_ms)
        ) {
          const id = event.delegation.id
          if (typeof id !== 'string' || tasks.has(id) || state.status === 'closing') return
          tasks.add(id)
          const version = inputVersion
          const delegatedSession = sessionId
          send(
            `delegation:${id}`,
            'session.thinking.append',
            'The quote assistant is working on the request.',
            id,
          )
          const started = (message: ChatMessage) => {
            if (currentRun !== run) return
            results.set(message.id, { sessionId: delegatedSession, delegation: id, spoken: '', version, message, working: true })
            result(message, true)
          }
          void options
            .delegate(sessionId, id, event.offset_ms, started)
            .then((message) => {
              if (currentRun !== run) return
              if (!message) {
                if (current === generation) {
                  send(
                    `delegation:${id}`,
                    'session.thinking.append',
                    'No new actionable user transcript is available. Do not repeat earlier actions; ask the user to clarify if needed.',
                    id,
                  )
                }
                return
              }
              if (!results.has(message.id)) started(message)
              result(message)
            })
            .catch(() => {
              if (currentRun === run) {
                send(
                  `delegation-error:${id}`,
                  'session.commentary.append',
                  'The request could not finish. Review the latest result below before retrying; earlier changes may already be saved.',
                  delegatedSession === sessionId ? id : null,
                  version,
                )
              }
            })
        }
      })
      await connection.setLocalDescription(await connection.createOffer())
      if (connection.iceGatheringState !== 'complete') {
        await new Promise<void>((resolve, reject) => {
          const finish = () => {
            connection.removeEventListener('icegatheringstatechange', check)
            signal.removeEventListener('abort', cancel)
            clearTimeout(iceTimeout)
          }
          const cancel = () => {
            finish()
            reject(new Error('canceled'))
          }
          const check = () => {
            if (connection.iceGatheringState === 'complete') {
              finish()
              resolve()
            }
          }
          const iceTimeout = setTimeout(cancel, 10000)
          connection.addEventListener('icegatheringstatechange', check)
          signal.addEventListener('abort', cancel)
          check()
        })
      }
      if (current !== generation) return
      const sdp = connection.localDescription?.sdp
      if (!sdp) throw new Error('missing offer')
      const context = await options.context()
      if (current !== generation) return
      const response = await createLiveSession(options.api, { ...context, sdp }, signal)
      if (current !== generation) return
      sessionId = response.session.id
      await connection.setRemoteDescription({ type: 'answer', sdp: response.transport.sdp })
    } catch (cause) {
      if (current !== generation) return
      const denied = cause instanceof DOMException && ['NotAllowedError', 'NotFoundError', 'AbortError'].includes(cause.name)
      const rejected =
        cause instanceof QuoteBuilderApiError && ([401, 403, 404, 422].includes(cause.status) || cause.code === 'live_not_configured')
      if (connected && !denied && !rejected) {
        reconnect()
        return
      }
      fail(
        cause instanceof Error && cause.message === 'insecure_page'
          ? 'Live voice needs the dashboard on a secure (https) address; the browser blocks the microphone here.'
          : cause instanceof Error && cause.message === 'unsupported'
            ? 'This browser does not allow Live voice. Use a current Chrome, Edge, Safari or Firefox.'
            : cause instanceof DOMException && cause.name === 'NotAllowedError'
          ? 'Allow microphone access in your browser, then try Live voice again.'
          : cause instanceof DOMException && cause.name === 'NotFoundError'
            ? 'Connect a microphone, then try Live voice again.'
            : 'Voice could not connect. Check your microphone and connection, then try again.',
      )
    }
  }
  return {
    start,
    stop,
    play,
    result,
    context: (content: string) => {
      currentContext = content
      send('context', 'session.thinking.append', content)
    },
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    dispose: () => {
      stop()
      microphone?.getTracks().forEach((track) => track.stop())
      audio?.pause()
      if (state.status !== 'closing') cleanup()
    },
  }
}

export async function liveConversationContext(conversation: AssistantConversation, fileIds: string[]) {
  const quoteId = conversation.getSnapshot().quote?.id || null
  if (quoteId && !conversation.getSnapshot().messages.length) await conversation.restore()
  const state = conversation.getSnapshot()
  const candidates = [
    ...state.messages.map((message) => ({
      order: message.responseOrder ?? message.id,
      role: message.role,
      content: [message.content, ...(message.actions || []).map((action) => `${action.title}: ${action.status}`)].join('\n'),
    })),
    ...state.transcripts.map((fragment) => ({ order: fragment.order ?? 0, role: fragment.role, content: fragment.delta })),
  ]
    .filter((item) => item.content)
    .sort((a, b) => a.order - b.order)
  const history: NonNullable<LiveSessionRequest['history']> = []
  let bytes = 0
  // Live startup has a provider history budget; the full conversation stays in the agent.
  for (const item of candidates.reverse()) {
    const content = item.content.slice(-2000)
    const size = new TextEncoder().encode(content).length
    if (bytes + size > 7500 || history.length === 100) break
    bytes += size
    history.unshift({ role: item.role, content })
  }
  return { quote_id: quoteId, file_ids: [...fileIds], history }
}

/** Live voice for the assistant chat. Quote files are not attached to assistant turns yet, so voice sends none. */
export function useLiveVoice(api: QuoteBuilderApi, conversation: AssistantConversation) {
  const [live] = useState(() =>
    createLiveVoice({
      api,
      context: () => liveConversationContext(conversation, []),
      transcript: conversation.addTranscript,
      delegate: (session, id, offset, started) => conversation.delegate(session, id, offset, [], started),
    }),
  )
  const state = useSyncExternalStore(live.subscribe, live.getSnapshot, live.getSnapshot)
  useEffect(() => () => live.dispose(), [live])
  const speaking = state.status === 'listening' || state.status === 'speaking'
  useEffect(() => {
    let previous = ''
    let timer: ReturnType<typeof setTimeout> | undefined
    const update = () => {
      const current = conversation.getSnapshot()
      current.messages.forEach((message) => live.result(message, current.pending > 0))
      const running = current.pending > 0 || current.messages.some((message) => message.running)
      const recent = current.messages
        .flatMap((message) => message.actions || [])
        .slice(-3)
        .map((action) => `${action.title}: ${action.status}`)
        .join('; ')
      const answer = current.messages.filter((message) => message.role === 'assistant').at(-1)?.content || ''
      const context = `Current quote: ${current.quote?.quote_number || current.quote?.title || 'none'}, revision ${current.quote?.revision ?? 0}. Auto-approve edits: ${current.autoApprove}. ${running ? 'Backend work is still running. Observe the existing worker; do not start another builder.' : 'Backend is not running.'} Actions: ${recent}. Latest backend answer: ${answer}.`
      if (context !== previous) {
        previous = context
        live.context(context)
      }
    }
    update()
    const unsubscribe = conversation.subscribe(() => {
      const current = conversation.getSnapshot()
      current.messages.forEach((message) => live.result(message, current.pending > 0))
      clearTimeout(timer)
      timer = setTimeout(update, 200)
    })
    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [conversation, live, speaking])
  return { ...state, start: live.start, stop: live.stop, play: live.play, active: !['idle', 'error'].includes(state.status) }
}

/** Transcript fragments joined into readable caption rows per speaker (demo `captionRows`). */
export function captionRows(fragments: LiveTranscript[]) {
  type Row = { id: string; sessionId: string; role: 'user' | 'assistant'; text: string; start: number; end: number; fragments: LiveTranscript[] }
  const rows: Row[] = []
  for (const fragment of fragments) {
    const row = [...rows]
      .reverse()
      .find(
        (item) =>
          item.role === fragment.role &&
          item.sessionId === fragment.sessionId &&
          fragment.start_ms <= item.end + 1500 &&
          fragment.end_ms >= item.start - 1500,
      )
    if (row) {
      row.fragments.push(fragment)
      row.text = row.fragments
        .sort((a, b) => a.start_ms - b.start_ms)
        .map((item) => item.delta)
        .join('')
      row.start = Math.min(row.start, fragment.start_ms)
      row.end = Math.max(row.end, fragment.end_ms)
    } else {
      rows.push({
        id: `${fragment.sessionId}:${fragment.event_id}`,
        sessionId: fragment.sessionId,
        role: fragment.role,
        text: fragment.delta,
        start: fragment.start_ms,
        end: fragment.end_ms,
        fragments: [fragment],
      })
    }
  }
  return rows
}
